import type { GridRole } from '../websocket'

export interface GridShellUser {
  readonly displayName: string
  readonly email: string
  readonly isAdmin: boolean
}

export type GridPanelDisposer = () => void
export type GridPanelOpener = (
  onClose: () => void,
) => void | GridPanelDisposer | Promise<void | GridPanelDisposer>

export interface GridShellOptions {
  readonly container: HTMLElement
  readonly focusGrid: () => void
  readonly requestRepaint: () => void
  readonly user: GridShellUser
  readonly openFiles?: GridPanelOpener
  readonly openSettings?: GridPanelOpener
  readonly openAdmin?: GridPanelOpener
  readonly onTakeControl: () => void
  readonly onLogout: () => void
}

export class GridShell {
  readonly #container: HTMLElement
  readonly #focusGrid: () => void
  readonly #requestRepaint: () => void
  readonly #user: GridShellUser
  readonly #openFiles?: GridPanelOpener
  readonly #openSettings?: GridPanelOpener
  readonly #openAdmin?: GridPanelOpener
  readonly #onTakeControl: () => void
  readonly #onLogout: () => void
  readonly #root: HTMLElement
  readonly #button: HTMLButtonElement
  readonly #menu: HTMLElement
  readonly #takeControl: HTMLButtonElement
  #panelOpen = false
  #panelDisposer: GridPanelDisposer | null = null
  #accountCleanup: (() => void) | null = null
  #disposed = false

  constructor(options: GridShellOptions) {
    this.#container = options.container
    this.#focusGrid = options.focusGrid
    this.#requestRepaint = options.requestRepaint
    this.#user = options.user
    this.#openFiles = options.openFiles
    this.#openSettings = options.openSettings
    this.#openAdmin = options.openAdmin
    this.#onTakeControl = options.onTakeControl
    this.#onLogout = options.onLogout

    this.#root = document.createElement('div')
    this.#root.className = 'grid-floating-shell'
    this.#root.innerHTML = `
      <button class="grid-menu-button" type="button" aria-label="Open menu" aria-haspopup="menu" aria-expanded="false">
        <span aria-hidden="true">☰</span>
      </button>
      <div class="grid-menu" role="menu" hidden>
        <button type="button" role="menuitem" data-grid-menu-action="files">Files</button>
        <button type="button" role="menuitem" data-grid-menu-action="settings">Settings</button>
        <button type="button" role="menuitem" data-grid-menu-action="admin">Admin</button>
        <button type="button" role="menuitem" data-grid-menu-action="account">Account</button>
        <button type="button" role="menuitem" data-grid-menu-action="take-control" hidden>Take Control</button>
      </div>
    `
    this.#container.appendChild(this.#root)
    this.#button = this.#root.querySelector('.grid-menu-button') as HTMLButtonElement
    this.#menu = this.#root.querySelector('.grid-menu') as HTMLElement
    this.#takeControl = this.#menu.querySelector('[data-grid-menu-action="take-control"]') as HTMLButtonElement
    const admin = this.#menu.querySelector('[data-grid-menu-action="admin"]') as HTMLButtonElement
    admin.hidden = !this.#user.isAdmin || this.#openAdmin === undefined

    this.#button.addEventListener('click', () => this.#toggleMenu())
    this.#action('files', this.#openFiles)
    this.#action('settings', this.#openSettings)
    this.#action('admin', this.#openAdmin)
    this.#menu.querySelector('[data-grid-menu-action="account"]')?.addEventListener('click', () => {
      this.#closeMenu()
      this.#openAccount()
    })
    this.#takeControl.addEventListener('click', () => {
      this.#closeMenu()
      this.#onTakeControl()
      this.#finishPanel()
    })
  }

  setRole(role: GridRole | null): void {
    this.#takeControl.hidden = role !== 'observer'
  }

  focusGridIfIdle(): void {
    if (this.#disposed || this.#panelOpen) return
    this.#focusGrid()
  }

  dispose(): void {
    if (this.#disposed) return
    this.#disposed = true
    this.#disposeActivePanel()
    this.#root.remove()
  }

  #action(name: 'files' | 'settings' | 'admin', opener: GridPanelOpener | undefined): void {
    const button = this.#menu.querySelector(`[data-grid-menu-action="${name}"]`) as HTMLButtonElement
    button.hidden = opener === undefined
    button.addEventListener('click', () => {
      if (opener === undefined || this.#disposed) return
      this.#closeMenu()
      this.#disposeActivePanel()
      this.#panelOpen = true
      let finished = false
      let disposeRequested = false
      let externalDisposed = false
      let externalDisposer: GridPanelDisposer | null = null
      let dispose: GridPanelDisposer
      const disposeExternal = () => {
        if (externalDisposed || externalDisposer === null) return
        externalDisposed = true
        externalDisposer()
      }
      const finish = () => {
        if (finished) return
        finished = true
        if (this.#panelDisposer === dispose) this.#panelDisposer = null
        try {
          disposeExternal()
        } finally {
          this.#finishPanel()
        }
      }
      dispose = () => {
        if (disposeRequested) return
        disposeRequested = true
        try {
          disposeExternal()
        } finally {
          finish()
        }
      }
      this.#panelDisposer = dispose
      try {
        const opened = opener(finish)
        if (typeof opened === 'function') {
          externalDisposer = opened
          if (disposeRequested || finished) disposeExternal()
        } else if (opened !== undefined) {
          void Promise.resolve(opened).then((resolved) => {
            if (typeof resolved !== 'function') return
            externalDisposer = resolved
            if (disposeRequested || finished) disposeExternal()
          }).catch(dispose)
        }
      } catch {
        dispose()
      }
    })
  }

  #toggleMenu(): void {
    const opening = this.#menu.hidden
    this.#menu.hidden = !opening
    this.#button.setAttribute('aria-expanded', String(opening))
    this.#root.classList.toggle('menu-open', opening)
  }

  #closeMenu(): void {
    this.#menu.hidden = true
    this.#button.setAttribute('aria-expanded', 'false')
    this.#root.classList.remove('menu-open')
  }

  #openAccount(): void {
    if (this.#disposed) return
    this.#disposeActivePanel()
    this.#panelOpen = true
    const panel = document.createElement('section')
    panel.className = 'grid-account-panel'
    panel.setAttribute('aria-label', 'Account')
    const header = document.createElement('div')
    header.className = 'grid-account-header'
    const title = document.createElement('h2')
    title.textContent = 'Account'
    const close = document.createElement('button')
    close.type = 'button'
    close.className = 'grid-account-close'
    close.setAttribute('aria-label', 'Close')
    close.textContent = '×'
    header.append(title, close)
    const content = document.createElement('div')
    content.className = 'grid-account-content'
    const name = document.createElement('strong')
    name.textContent = this.#user.displayName
    const email = document.createElement('span')
    email.textContent = this.#user.email
    const logout = document.createElement('button')
    logout.type = 'button'
    logout.className = 'grid-account-logout'
    logout.textContent = 'Log Out'
    content.append(name, email, logout)
    panel.append(header, content)
    this.#container.appendChild(panel)

    let finished = false
    const finish = () => {
      if (finished) return
      finished = true
      document.removeEventListener('keydown', escape)
      panel.remove()
      if (this.#accountCleanup === finish) this.#accountCleanup = null
      this.#finishPanel()
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      finish()
    }
    close.addEventListener('click', finish)
    logout.addEventListener('click', () => this.#onLogout())
    document.addEventListener('keydown', escape)
    this.#accountCleanup = finish
    close.focus()
  }

  #disposeActivePanel(): void {
    const dispose = this.#panelDisposer
    this.#panelDisposer = null
    dispose?.()
    this.#accountCleanup?.()
  }

  #finishPanel(): void {
    this.#panelOpen = false
    if (this.#disposed) return
    this.#requestRepaint()
    this.#focusGrid()
  }
}
